import { Maybe, TAsset, TAssetGroup } from '@types';
import { useEffect, useMemo, useState } from 'react';

type TGroup = {
  assetGroup: Maybe<TAssetGroup>;
  prevAssetGroup: Maybe<TAssetGroup>;
  groups: TAssetGroup[];
  assets: TAsset[];
};

type Props = {
  flatten?: boolean;
  assetGroups: TAssetGroup[];
  assets: TAsset[];
  activeAsset: Maybe<TAsset>;
};

const defaultProps: Props = {
  flatten: false,
  assetGroups: [],
  assets: [],
  activeAsset: null,
};

const usePartnerAssetGroups = ({
  flatten = false,
  assetGroups = [],
  assets: _assets = [],
  activeAsset = null,
}: Props = defaultProps) => {
  const [level, setLevel] = useState(1);
  const assets = useMemo(() => _assets ?? [], [_assets]);

  const getRootGroupsAndAssets = (parentId = null) => {
    let rootGroups: TAssetGroup[] = [];
    let rootAssets: TAsset[] = [];

    if (!parentId) {
      // if no parent id, get all groups that have no parents
      rootGroups = assetGroups.filter(
        (g) =>
          !g.parents ||
          g?.parents?.length === 0 ||
          assetGroups.every((g2) => !g.parents || g.parents.indexOf(g2.id) === -1)
      );

      rootAssets = assets.filter((a) => !a.groups || a.groups.length === 0);
    } else {
      // if parent id, get all groups that have parent id as a root group
      rootGroups = assetGroups.filter((g) => g?.parents?.includes(parentId));
      rootAssets = assets.filter((a) => a.groups && a.groups.indexOf(parentId) !== -1);
    }

    return { rootGroups, rootAssets };
  };

  const groupLevelsMap = useMemo(() => {
    const levels = {};

    const rootGroupsAndAssets = getRootGroupsAndAssets();
    for (const rootGroup of rootGroupsAndAssets.rootGroups) {
      levels[rootGroup.id] = 1;
      const childGroups = assetGroups.filter((g) => g.parents && g.parents.indexOf(rootGroup.id) !== -1);
      for (const childGroup of childGroups) {
        levels[childGroup.id] = 2;
        // const grandChildGroups = assetGroups.filter(
        //   (g) => g.parents && g.parents.indexOf(childGroup.id) !== -1
        // );
        // for (const grandChildGroup of grandChildGroups) {
        //   levels[grandChildGroup.id] = 3;
        // }
      }
    }
    return levels;
  }, [assetGroups]);

  const [rootGroup, setRootGroup] = useState<TGroup>({
    assetGroup: null,
    prevAssetGroup: null,
    groups: [],
    assets: [],
  });
  const [activeGroup, setActiveGroup] = useState<Maybe<TGroup>>(null);

  const selectGroup = (assetGroup: Maybe<TAssetGroup>) => () => {
    // If assetGroup is null set active group to null to render rootGroup
    if (!assetGroup) {
      setActiveGroup(null);
      setLevel(1);
      return;
    }

    const _activeGroup: TGroup = {
      assetGroup,
      groups: assetGroups.filter((g) => g.parents && g.parents.indexOf(assetGroup.id) !== -1),
      assets: [],
      prevAssetGroup: assetGroups.find((g) => assetGroup.parents.indexOf(g.id) >= 0) ?? null,
    };

    _activeGroup.assets = assets.filter(
      (a) =>
        a.groups &&
        a.groups.indexOf(assetGroup.id) !== -1 &&
        !a.groups.some((g) => _activeGroup.groups.some((g2) => g2.id === g))
    );

    setActiveGroup(_activeGroup);
    setLevel(groupLevelsMap[assetGroup?.id ?? ''] ?? 3);
  };

  const selectFlattenedGroup = (assetGroup: Maybe<TAssetGroup>) => () => {
    const rootGroups = assetGroups.filter(
      (g) =>
        !g.parentId ||
        !g.parents ||
        g.parents.length === 0 ||
        assetGroups.every((g2) => g2.id !== g?.parentId && (!g.parents || g.parents.indexOf(g2.id) === -1))
    );

    const assets: TAsset[] = [];

    if (assetGroup) assets.push(..._flattenAssets(assetGroup));
    else rootGroups.forEach((g) => assets.push(..._flattenAssets(g)));

    const _activeGroup: TGroup = {
      assetGroup: assetGroup ?? null,
      groups: rootGroups,
      assets,
      prevAssetGroup: null,
    };
    setActiveGroup(_activeGroup);
    setLevel(groupLevelsMap[assetGroup?.id ?? ''] ?? 3);
  };

  const _flattenAssets = (assetGroup: TAssetGroup) => {
    const _activeFlattenedAssets = [];
    const _activeGroup: TGroup = {
      assetGroup,
      groups: assetGroups.filter(
        (g) => g.parentId === assetGroup.id || (g.parents && g.parents.indexOf(assetGroup.id) !== -1)
      ),
      assets: assets.filter((a) => a.groups && a.groups.indexOf(assetGroup.id) !== -1),
      prevAssetGroup:
        assetGroups.find((g) => g.id === assetGroup.parentId || assetGroup.parents.indexOf(g.id) >= 0) ??
        null,
    };

    _activeFlattenedAssets.push(..._activeGroup.assets);

    for (const group of _activeGroup.groups) {
      _activeFlattenedAssets.push(..._flattenAssets(group));
    }

    return _activeFlattenedAssets;
  };

  useEffect(() => {
    if (!assets.length || !assetGroups.length) return;

    let parentId = null;
    // get root groups and assets
    let { rootGroups, rootAssets } = getRootGroupsAndAssets(parentId);

    // when a group has only one child group and no assets, get the child group's root groups and assets
    while (rootGroups.length === 1 && rootAssets.length === 0) {
      parentId = rootGroups[0].id;
      const result = getRootGroupsAndAssets(parentId);
      rootGroups = result.rootGroups;
      rootAssets = result.rootAssets;
    }

    setRootGroup({
      assetGroup: null,
      prevAssetGroup: null,
      groups: rootGroups,
      assets: rootAssets,
    });
  }, [assetGroups, assets]);

  // Auto select group based on selected asset on page load
  const qs = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(location.search).entries());
  }, []);

  useEffect(() => {
    if (!qs.selectGroup) return;

    if (!activeAsset) return;

    const assetGroup = assetGroups.find((g) => g.id === activeAsset?.groups?.[0]);
    if (!assetGroup) return;

    // When flatten is true, we always want to select the root group
    flatten ? selectFlattenedGroup(null)() : selectGroup(assetGroup)();
  }, [activeAsset, flatten, qs.selectGroup]);

  return {
    activeGroup: activeGroup || rootGroup,
    selectGroup: flatten ? selectFlattenedGroup : selectGroup,
    flattenAssets: _flattenAssets,
    level,
    rootGroup,
  };
};

export default usePartnerAssetGroups;
